/**
 * Performance Context Provider - Unified performance state management
 * Phase 1: Performance Provider Foundation
 * 
 * Consolidates Web Vitals, memory usage, and network conditions into single provider
 * Maintains backward compatibility with existing monitoring components
 */

import React from 'react';
import { productionLogger } from '@/utils/production-logger';
import { CORE_WEB_VITALS, PERFORMANCE_SCORE, MEMORY_THRESHOLDS } from '@/constants/performance.constants';
import { serviceWorkerIntegration } from '@/utils/serviceWorkerIntegration';
import type { PerformanceMetrics, WebVitalsEntry, PerformanceReport } from '@/types/performance.types';

interface OptimizationMetrics {
  lazyImages: { originalSize: number; optimizedSize: number; count: number };
  webpConversion: { originalSize: number; optimizedSize: number; count: number };
  criticalCSS: { renderBlockingReduction: number };
  totalSavings: number;
}

interface PerformanceContextType {
  // Core metrics
  metrics: PerformanceMetrics;
  optimizationMetrics: OptimizationMetrics;
  
  // Performance score and grade
  performanceScore: number;
  performanceGrade: 'A' | 'B' | 'C' | 'D' | 'F';
  
  // State management
  isMonitoring: boolean;
  lastUpdated: string;
  
  // Actions
  startMonitoring: () => void;
  stopMonitoring: () => void;
  recordWebVital: (vital: WebVitalsEntry) => void;
  recordOptimization: (type: keyof OptimizationMetrics, data: any) => void;
  updateMetrics: (newMetrics: Partial<PerformanceMetrics>) => void;
  getPerformanceReport: () => PerformanceReport;
  
  // Memory management
  clearMetrics: () => void;
  getMemoryUsage: () => number;
}

// Create context with proper initialization - move after React import is fully resolved
let PerformanceContext: React.Context<PerformanceContextType | undefined>;

const initialMetrics: PerformanceMetrics = {
  lcp: undefined,
  fid: undefined,
  cls: undefined,
  loadTime: 0,
  memoryUsage: 0,
  batteryLevel: undefined,
  networkType: undefined,
  deviceType: 'desktop',
  connection: undefined,
  resourceCount: 0,
  interactionLatency: 0,
};

const initialOptimizationMetrics: OptimizationMetrics = {
  lazyImages: { originalSize: 0, optimizedSize: 0, count: 0 },
  webpConversion: { originalSize: 0, optimizedSize: 0, count: 0 },
  criticalCSS: { renderBlockingReduction: 0 },
  totalSavings: 0,
};

interface PerformanceProviderProps {
  children: React.ReactNode;
  enableAutoMonitoring?: boolean;
}

export const PerformanceProvider: React.FC<PerformanceProviderProps> = ({ 
  children, 
  enableAutoMonitoring = true 
}) => {
  // Initialize context here to avoid hoisting issues
  if (!PerformanceContext) {
    PerformanceContext = React.createContext<PerformanceContextType | undefined>(undefined);
  }
  const [metrics, setMetrics] = React.useState<PerformanceMetrics>(initialMetrics);
  const [optimizationMetrics, setOptimizationMetrics] = React.useState<OptimizationMetrics>(initialOptimizationMetrics);
  const [isMonitoring, setIsMonitoring] = React.useState(false);
  const [lastUpdated, setLastUpdated] = React.useState(new Date().toISOString());

  // Calculate performance score based on Web Vitals
  const performanceScore = React.useMemo(() => {
    let score = PERFORMANCE_SCORE.BASE_SCORE;
    
    // LCP penalties
    if (metrics.lcp) {
      if (metrics.lcp > CORE_WEB_VITALS.LCP.POOR) score -= 30;
      else if (metrics.lcp > CORE_WEB_VITALS.LCP.GOOD) score -= 15;
    }
    
    // FID penalties  
    if (metrics.fid) {
      if (metrics.fid > CORE_WEB_VITALS.FID.POOR) score -= 25;
      else if (metrics.fid > CORE_WEB_VITALS.FID.GOOD) score -= 10;
    }
    
    // CLS penalties
    if (metrics.cls) {
      if (metrics.cls > CORE_WEB_VITALS.CLS.POOR) score -= 20;
      else if (metrics.cls > CORE_WEB_VITALS.CLS.GOOD) score -= 10;
    }
    
    // Memory penalties
    if (metrics.memoryUsage > MEMORY_THRESHOLDS.HIGH) score -= 15;
    else if (metrics.memoryUsage > MEMORY_THRESHOLDS.MEDIUM) score -= 8;
    
    return Math.max(0, Math.min(100, score));
  }, [metrics]);

  // Calculate performance grade
  const performanceGrade: 'A' | 'B' | 'C' | 'D' | 'F' = React.useMemo(() => {
    if (performanceScore >= 90) return 'A';
    if (performanceScore >= 80) return 'B';
    if (performanceScore >= 70) return 'C';
    if (performanceScore >= 60) return 'D';
    return 'F';
  }, [performanceScore]);

  // Setup performance observers
  const setupPerformanceObservers = React.useCallback(() => {
    if (!isMonitoring || typeof window === 'undefined') return;

    try {
      // Web Vitals observer
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.entryType === 'largest-contentful-paint') {
            recordWebVital({
              name: 'LCP',
              value: entry.startTime,
              delta: entry.startTime,
              id: 'lcp-' + Date.now(),
              entries: [entry]
            });
          }
          
          if (entry.entryType === 'first-input') {
            const fidEntry = entry as PerformanceEventTiming;
            recordWebVital({
              name: 'FID',
              value: fidEntry.processingStart - fidEntry.startTime,
              delta: fidEntry.processingStart - fidEntry.startTime,
              id: 'fid-' + Date.now(),
              entries: [entry]
            });
          }
          
          if (entry.entryType === 'layout-shift' && !(entry as any).hadRecentInput) {
            recordWebVital({
              name: 'CLS',
              value: (entry as any).value,
              delta: (entry as any).value,
              id: 'cls-' + Date.now(),
              entries: [entry]
            });
          }
        }
      });

      observer.observe({ entryTypes: ['largest-contentful-paint', 'first-input', 'layout-shift'] });

      return () => observer.disconnect();
    } catch (error) {
      productionLogger.error('Failed to setup performance observers:', error);
    }
  }, [isMonitoring]);

  // Collect device and network metrics
  const collectDeviceMetrics = React.useCallback(async () => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return;

    try {
      // Memory usage
      const memoryUsage = (performance as any).memory ? 
        ((performance as any).memory.usedJSHeapSize / (performance as any).memory.jsHeapSizeLimit) * 100 : 0;

      // Network information
      const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
      
      // Battery information
      let batteryLevel: number | undefined;
      try {
        const battery = await (navigator as any).getBattery?.();
        batteryLevel = battery ? battery.level * 100 : undefined;
      } catch (e) {
        // Battery API not supported
      }

      // Device type detection
      const deviceType: 'mobile' | 'tablet' | 'desktop' = 
        window.innerWidth < 768 ? 'mobile' : 
        window.innerWidth < 1024 ? 'tablet' : 'desktop';

      setMetrics(prev => ({
        ...prev,
        memoryUsage,
        batteryLevel,
        networkType: connection?.effectiveType,
        deviceType,
        connection: connection ? {
          effectiveType: connection.effectiveType,
          downlink: connection.downlink,
          rtt: connection.rtt
        } : undefined,
        resourceCount: performance.getEntriesByType('resource').length
      }));

      setLastUpdated(new Date().toISOString());
    } catch (error) {
      productionLogger.error('Failed to collect device metrics:', error);
    }
  }, []);

  // Start monitoring
  const startMonitoring = React.useCallback(() => {
    setIsMonitoring(true);
    collectDeviceMetrics();
    productionLogger.info('🎯 Performance monitoring started');
  }, [collectDeviceMetrics]);

  // Stop monitoring  
  const stopMonitoring = React.useCallback(() => {
    setIsMonitoring(false);
    productionLogger.info('⏸️ Performance monitoring stopped');
  }, []);

  // Record Web Vital
  const recordWebVital = React.useCallback((vital: WebVitalsEntry) => {
    setMetrics(prev => ({
      ...prev,
      [vital.name.toLowerCase()]: vital.value
    }));
    
    productionLogger.info(`📊 ${vital.name}: ${vital.value}`);
    setLastUpdated(new Date().toISOString());
  }, []);

  // Record optimization metrics
  const recordOptimization = React.useCallback((type: keyof OptimizationMetrics, data: any) => {
    setOptimizationMetrics(prev => {
      const updated = { ...prev };
      
      if (type === 'lazyImages' || type === 'webpConversion') {
        updated[type] = {
          originalSize: prev[type].originalSize + data.originalSize,
          optimizedSize: prev[type].optimizedSize + data.optimizedSize,
          count: prev[type].count + 1
        };
      } else if (type === 'criticalCSS') {
        updated[type] = { renderBlockingReduction: data.renderBlockingReduction };
      }
      
      // Recalculate total savings
      updated.totalSavings = 
        (updated.lazyImages.originalSize - updated.lazyImages.optimizedSize) +
        (updated.webpConversion.originalSize - updated.webpConversion.optimizedSize) +
        updated.criticalCSS.renderBlockingReduction;
      
      return updated;
    });
    
    setLastUpdated(new Date().toISOString());
  }, []);

  // Update metrics (for service worker integration)
  const updateMetrics = React.useCallback((newMetrics: Partial<PerformanceMetrics>) => {
    setMetrics(prev => ({ ...prev, ...newMetrics }));
    setLastUpdated(new Date().toISOString());
  }, []);

  // Generate performance report
  const getPerformanceReport = React.useCallback((): PerformanceReport => ({
    score: performanceScore,
    grade: performanceGrade,
    metrics,
    issues: [], // TODO: Implement issue detection
    timestamp: lastUpdated,
    deviceInfo: {
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
      viewport: {
        width: typeof window !== 'undefined' ? window.innerWidth : 0,
        height: typeof window !== 'undefined' ? window.innerHeight : 0
      },
      connectionType: metrics.networkType
    }
  }), [performanceScore, performanceGrade, metrics, lastUpdated]);

  // Clear metrics
  const clearMetrics = React.useCallback(() => {
    setMetrics(initialMetrics);
    setOptimizationMetrics(initialOptimizationMetrics);
    setLastUpdated(new Date().toISOString());
    productionLogger.info('🧹 Performance metrics cleared');
  }, []);

  // Get current memory usage
  const getMemoryUsage = React.useCallback((): number => {
    if (typeof window === 'undefined' || !(performance as any).memory) return 0;
    return ((performance as any).memory.usedJSHeapSize / 1024 / 1024); // MB
  }, []);

  // Auto-start monitoring and initialize service worker integration
  React.useEffect(() => {
    if (enableAutoMonitoring) {
      startMonitoring();
      
      // Initialize service worker integration
      serviceWorkerIntegration.initialize((cacheMetrics) => {
        updateMetrics({
          cacheHitRate: cacheMetrics.cacheHitRate,
          totalCacheRequests: cacheMetrics.totalCacheRequests,
          cacheErrors: cacheMetrics.cacheErrors
        });
      });
    }
  }, [enableAutoMonitoring, startMonitoring, updateMetrics]);

  // Setup observers when monitoring starts
  React.useEffect(() => {
    if (isMonitoring) {
      return setupPerformanceObservers();
    }
  }, [isMonitoring, setupPerformanceObservers]);

  // Periodic metrics update
  React.useEffect(() => {
    if (!isMonitoring) return;

    const interval = setInterval(collectDeviceMetrics, 5000);
    return () => clearInterval(interval);
  }, [isMonitoring, collectDeviceMetrics]);

  const contextValue: PerformanceContextType = {
    metrics,
    optimizationMetrics,
    performanceScore,
    performanceGrade,
    isMonitoring,
    lastUpdated,
    startMonitoring,
    stopMonitoring,
    recordWebVital,
    recordOptimization,
    updateMetrics,
    getPerformanceReport,
    clearMetrics,
    getMemoryUsage
  };

  return (
    <PerformanceContext.Provider value={contextValue}>
      {children}
    </PerformanceContext.Provider>
  );
};

// Custom hook to use performance context
export const usePerformanceContext = (): PerformanceContextType => {
  const context = React.useContext(PerformanceContext);
  if (context === undefined) {
    throw new Error('usePerformanceContext must be used within a PerformanceProvider');
  }
  return context;
};